import React, { FC, useEffect, useState, useRef } from 'react';
import ContentBlock from 'components/ContentBlock/Index';
import AboutMeContent from 'components/shared/AboutMe/Index';
import ExtraActivities from 'components/shared/ExtraActivities';
import SocialMediaExpertise from 'components/shared/SocialMediaExpertise';
import AboutMePopUp from 'components/shared/Popups/AboutMe/Index';
import Popup from 'components/shared/Popups/Popup/Index';
import SocialMediaExpertisePopup from 'components/shared/Popups/SocialMediaExpertise/Index';
import ExtraActivitiesPopup from 'components/shared/Popups/ExtraActivities/Index';
import { IAboutMeTab, IAboutUser } from 'core/interface/aboutMe';
import { LanguageService } from 'services/language';
import { CandidateService } from 'services/candidate/candidate';
import { useSelector } from 'react-redux';
import { Gender } from 'core/enum/gender';
import { ExtraActivitieService } from 'services/extraActivities/extraActivities';
import { SocialMediaExpertiseService } from 'services/socialMediaExpertise/socialMediaExpertise';
import {
  ISocialMediaData,
  ISocialMediaItems
} from 'core/interface/socialMedia';
import { ProfileService } from 'services/profile/profile';
import { SocialMediaEnum } from 'core/enum/social-media';
import { AuthService } from 'services/auth/auth';
import { SocialMediaRedux } from 'core/interface/socialMediaExpertise';
import { RootState } from 'redux/store';

import './Index.scss';

const ProfileAbout: FC<IAboutMeTab & { noMargin?: boolean }> = ({
  t,
  candidate_id,
  fromGuest,
  noMargin
}): JSX.Element => {
  const { socialMedia } = useSelector(
    (state: SocialMediaRedux) => state.socialMediaExpertise
  );
  const { candidate_language } = useSelector(
    (state: RootState) => state.languages
  );
  const { extraActivities } = useSelector(
    (state: RootState) => state.extraActivites
  );
  const { candidates } = useSelector((state: RootState) => state.candidates);
  const [userAbout, setUserAbout] = useState<IAboutUser[]>([]);
  const [socialMediaExpertise, setSocialMediaExpertise] = useState<
    ISocialMediaItems[]
  >([]);
  const [socialNetwork, setSocialNetwork] = useState<string>('');
  const [showAboutMePopup, setShowAboutMePopup] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [showExpertisePopup, setShowExpertisePopup] = useState<boolean>(false);
  const [showExtraActivitiesPopup, setShowExtraActivitiesPopup] =
    useState<boolean>(false);
  const { socialMedia: media } = useSelector(
    (state: RootState) => state.socialMedia
  );
  const prevCandidateId = useRef<number | null>(null);

  useEffect(() => {
    if (candidate_id) {
      getLanguages();
      getCandidate();
      getSocialMedias();
      getExtraActivities();
      getSocialMediaExpertise();
      prevCandidateId.current = candidate_id;
    }
  }, [candidate_id]);

  useEffect(() => {
    setMyData();
  }, [candidates, socialNetwork, candidate_language]);

  const getCandidate = () => {
    CandidateService.getCandidate(candidate_id);
  };

  const handleToggle = (
    stateSetter: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    return () => stateSetter((prevState) => !prevState);
  };

  const setMyData = async () => {
    const candidate = [
      {
        icon: 'email.svg',
        value: candidates?.email,
        iconSize: 12
      },
      {
        icon: 'calendar.svg',
        value: candidates?.birthday,
        iconSize: 12
      },
      {
        icon: 'gender.svg',
        value: Gender[candidates?.gender],
        iconSize: 12
      },
      {
        icon: 'location.svg',
        value: candidates?.locations?.[0]?.display_name,
        iconSize: 12
      },
      {
        icon: 'phone.svg',
        value: candidates?.phone,
        iconSize: 12
      },
      {
        icon: 'personal-url.svg',
        value: candidates?.personal_url,
        iconSize: 12
      }
    ].filter((item) => item.value) as IAboutUser[];
    setUserAbout(candidate);
  };

  const onClose = (): void => {
    setShowAboutMePopup(false);
    setShowExpertisePopup(false);
    setShowExtraActivitiesPopup(false);
    setIsEdit(false);
    AuthService.getAccount();
  };

  const getExtraActivities = (): void => {
    ExtraActivitieService.getExtraActivities(candidate_id);
  };

  const getSocialMediaExpertise = () => {
    SocialMediaExpertiseService.getSocialMediaExpertise(candidate_id);
  };

  useEffect(() => {
    const items = [] as ISocialMediaItems[];
    socialMedia?.forEach((item: any) => {
      const socialItem = {
        id: item.id,
        icon: SocialMediaEnum[
          item.network.toLocaleLowerCase() as keyof typeof SocialMediaEnum
        ],

        level: item.proficiency,
        title: item.network
      } as ISocialMediaItems;
      items.push(socialItem);
    });
    setSocialMediaExpertise(items);
  }, [socialMedia]);

  const getLanguages = (): void => {
    LanguageService.getCandidateLanguage(candidate_id);
  };

  useEffect(() => {
    const socialNetworks = media?.reduce(
      (acc: string, elem: ISocialMediaData) => {
        acc += elem.network + ',';
        return acc;
      },
      ''
    );
    setSocialNetwork(socialNetworks);
  }, [media]);

  const getSocialMedias = async () => {
    ProfileService.getSocialMedias(candidate_id);
  };

  return (
    <div className="about">
      <ContentBlock
        className="about__left"
        title={t('About me')}
        onActionClick={handleToggle(setShowAboutMePopup)}
        noData={userAbout.length === 0 || !userAbout}
        showButton={fromGuest ? false : true}
      >
        <AboutMeContent
          surname={candidates?.last_name}
          name={fromGuest ? candidates?.name : candidates?.first_name}
          about={candidates?.pitch ?? ''}
          aboutMe={userAbout}
        />
      </ContentBlock>

      <div className="about__right">
        <div
          className="about__right__bottom"
          style={{ marginTop: noMargin ? '0' : '20px' }}
        >
          <ContentBlock
            className="about__right__bottom__left"
            title={t('Extra activities')}
            onActionClick={handleToggle(setShowExtraActivitiesPopup)}
            noData={extraActivities?.length === 0 || !extraActivities}
            showButton={fromGuest ? false : true}
          >
            <ExtraActivities />
          </ContentBlock>
          <ContentBlock
            className="about__right__bottom__right"
            title={t('Social media expertise')}
            isScrollable={true}
            heigth="120px"
            onActionClick={handleToggle(setShowExpertisePopup)}
            noData={socialMediaExpertise.length === 0 || !socialMediaExpertise}
            showButton={fromGuest ? false : true}
          >
            <SocialMediaExpertise socialMedia={socialMediaExpertise} />
          </ContentBlock>
        </div>
      </div>
      {showAboutMePopup && (
        <Popup onClose={onClose} title={t('About Me')}>
          <AboutMePopUp t={t} onModalClose={() => onClose()} />
        </Popup>
      )}
      {showExpertisePopup && (
        <Popup
          className="expertise"
          onClose={onClose}
          title={t('Social media expertise')}
        >
          <SocialMediaExpertisePopup
            t={t}
            socialMedias={socialMediaExpertise}
            onClose={onClose}
          />
        </Popup>
      )}
      {showExtraActivitiesPopup && (
        <Popup
          className={`${isEdit ? 'extra' : ''}`}
          title={t('Extra activities')}
          onClose={onClose}
        >
          <ExtraActivitiesPopup isExtra={setIsEdit} t={t} onClose={onClose} />
        </Popup>
      )}
      {/* <Popup className='confirm' title='Are you sure?' onClose={onClose}>
         <ConfirmPopup description='Do you really want to delete this? De vacature?' onApprove={() => {}} onCancel={() => {}}/>
        </Popup> */}
    </div>
  );
};

export default ProfileAbout;
